import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import PropertyCard from '../components/PropertyCard';
import MapboxMap from '../components/MapboxMap';
import SEO from '../components/SEO';
import { PUERTO_RICO_CITIES } from '../utils/constants';
import { initializeFiltersFromURL, SOLD_FILTER_KEYS } from '../utils/filterUtils';
import './PropertyList.css';

const SoldProperties = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize filters from URL query params
  const [filters, setFilters] = useState(() => 
    initializeFiltersFromURL(location.search, SOLD_FILTER_KEYS)
  );
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Update filters when URL changes (for navigation between pages)
  useEffect(() => {
    setFilters(initializeFiltersFromURL(location.search, SOLD_FILTER_KEYS));
  }, [location.search]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('listingType', 'sold');
        params.append('page', pagination.page.toString());
        params.append('limit', '15');
        if (filters.propertyType) params.append('propertyType', filters.propertyType);
        if (filters.minPrice) params.append('minPrice', filters.minPrice);
        if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
        if (filters.isReposeida !== '') params.append('isReposeida', filters.isReposeida);
        if (filters.city) params.append('city', filters.city);

        const response = await axios.get(`/api/properties?${params.toString()}`);
        
        // Filter client-side for bedrooms and bathrooms
        let filteredProperties = response.data.properties || response.data;
        
        if (filters.bedrooms) {
          const minBedrooms = Number(filters.bedrooms);
          filteredProperties = filteredProperties.filter(prop => prop.bedrooms >= minBedrooms);
        }
        
        if (filters.bathrooms) {
          const minBathrooms = Number(filters.bathrooms);
          filteredProperties = filteredProperties.filter(prop => prop.bathrooms >= minBathrooms);
        }
        
        setProperties(filteredProperties);
        
        // Update pagination info if available in response
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [filters, pagination.page]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mapContainerStyle = {
    width: '100%',
    height: '600px',
  };

  // Find the first property with valid coordinates for map center
  const propertyWithCoordinates = properties.find(
    p => p.coordinates?.lat !== 0 && p.coordinates?.lng !== 0
  );

  const center = propertyWithCoordinates
    ? { lat: propertyWithCoordinates.coordinates.lat, lng: propertyWithCoordinates.coordinates.lng }
    : { lat: 18.2208, lng: -66.5901 };

  // Prepare markers for map view
  const mapMarkers = properties
    .filter(p => p.coordinates?.lat !== 0 && p.coordinates?.lng !== 0)
    .map(property => ({
      id: property.id,
      title: property.title,
      price: property.price,
      coordinates: property.coordinates
    }));

  return (
    <div className="property-list">
      <SEO 
        title="Propiedades Vendidas en Puerto Rico | Portafolio de Ventas | Alicea Realty Group LLC"
        description="Vea nuestro portafolio de propiedades vendidas exitosamente en Puerto Rico. Testimonios de éxito con Noemí Alicea González, Corredora Autorizada certificada."
        keywords="propiedades vendidas Puerto Rico, casas vendidas Puerto Rico, ventas exitosas bienes raíces Puerto Rico, portafolio realtor Puerto Rico"
        ogTitle="Propiedades Vendidas en Puerto Rico | Alicea Realty Group LLC"
        ogDescription="Vea nuestro portafolio de propiedades vendidas exitosamente en Puerto Rico."
        canonical="https://www.alicearealtypr.com/properties/sold"
      />
      
      <div className="property-list-header">
        <h1>Propiedades Vendidas</h1>
        <div className="view-toggle">
          <button 
            className={viewMode === 'grid' ? 'active' : ''} 
            onClick={() => setViewMode('grid')}
          >
            Vista de Cuadrícula
          </button>
          <button 
            className={viewMode === 'map' ? 'active' : ''} 
            onClick={() => setViewMode('map')}
          >
            Vista de Mapa
          </button>
        </div>
      </div>

      <div className="filters">
        <select name="propertyType" value={filters.propertyType} onChange={handleFilterChange}>
          <option value="">Todos los Tipos</option>
          <option value="house">Casa</option>
          <option value="apartment">Apartamento</option>
          <option value="condo">Condominio</option>
          <option value="walkup">Walk Up</option>
          <option value="multifamiliar">Multifamiliar</option>
          <option value="finca">Finca</option>
          <option value="duplex">Duplex</option>
          <option value="land">Terreno</option>
          <option value="commercial">Comercial</option>
        </select>

        <select name="city" value={filters.city} onChange={handleFilterChange}>
          <option value="">Todas las Ciudades</option>
          {PUERTO_RICO_CITIES.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>

        <select name="bedrooms" value={filters.bedrooms} onChange={handleFilterChange}>
          <option value="">Todas las Habitaciones</option>
          <option value="1">1+</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
          <option value="5">5+</option>
        </select>

        <select name="bathrooms" value={filters.bathrooms} onChange={handleFilterChange}>
          <option value="">Todos los Baños</option>
          <option value="1">1+</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
        </select>

        <select name="isReposeida" value={filters.isReposeida} onChange={handleFilterChange}>
          <option value="">Todas (Reposeidas y No Reposeidas)</option>
          <option value="true">Solo Reposeidas</option>
          <option value="false">No Reposeidas</option>
        </select>

        <input
          type="number"
          name="minPrice"
          placeholder="Precio Mínimo"
          value={filters.minPrice}
          onChange={handleFilterChange}
        />

        <input
          type="number"
          name="maxPrice"
          placeholder="Precio Máximo"
          value={filters.maxPrice}
          onChange={handleFilterChange}
        />
      </div>

      {loading ? (
        <div className="loading">Cargando propiedades...</div>
      ) : viewMode === 'grid' ? (
        <>
          <div className="properties-grid">
            {properties.length > 0 ? (
              properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))
            ) : (
              <p className="no-properties">No se encontraron propiedades vendidas</p>
            )}
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="pagination-btn"
              >
                Anterior
              </button>
              <span className="pagination-info">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} propiedades)
              </span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="pagination-btn"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="map-view">
          <MapboxMap
            center={center}
            markers={mapMarkers}
            style={mapContainerStyle}
            zoom={12}
          />
        </div>
      )}
    </div>
  );
};

export default SoldProperties;
